// AmsQueue.tsx — the work queue.
//
// THE ORDERING IS THE POINT, and every row shows which of the two produced its score:
//
//   Category 1 is ordered by URGENCY — evidence expiring, months climbing, no engagement.
//   Everything else is ordered by RECOVERABILITY.
//
// Ranking hardship cases by likelihood of payment would push the residents least able to pay to
// the bottom of the queue, which is the opposite of the plan's own principle that collection is
// also a service. `priorityBasis` is rendered rather than hidden because an officer must be able
// to say why this resident was contacted today.
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type ArrearsCase } from '../../lib/api'
import { ageOf, BUCKETS, CATEGORIES, money, ROLE_LABELS } from '../../lib/format'
import DataTable, { type Column } from '../DataTable'

const DIVISIONS = ['NSC', 'NSE', 'CP', 'NSL', 'NSS']

/**
 * The columns, declared once and rendered as a table on a desktop and as stacked cards on a phone.
 *
 * The phone roles are chosen from what an officer scanning a queue is actually doing: they are
 * looking for WHO and WHERE (primary + secondary), judging HOW BAD at a glance (trailing carries
 * the category tag), and then reading the figures. Nothing is dropped on the small screen — a
 * column an officer can see at a desk is one they can see at a door, because a case half-read is
 * how the wrong letter gets sent.
 */
const COLUMNS = (rowAge: (asOf: string | null) => string | null): Column<ArrearsCase>[] => [
  {
    key: 'resident', header: 'Resident', role: 'primary',
    cell: c => <Link to={`/ams/cases/${c.id}`}>{c.residentName}</Link>,
  },
  { key: 'address', header: 'Address', role: 'secondary', cell: c => c.address },
  {
    key: 'category', header: 'Category', role: 'trailing',
    cell: c => (
      <span className={`tag c${c.category ?? 'u'}`}>
        {CATEGORIES[c.category ?? 'unsegmented']}{c.hardshipCode ? ` · ${c.hardshipCode}` : ''}
      </span>
    ),
  },
  { key: 'owed', header: 'Owed', num: true, cell: c => money(c.balanceCents) },
  { key: 'days', header: 'Days', num: true, cell: c => <b>{c.daysOverdue}</b> },
  // Months of charge is the MND reporting basis. It is shown, and it drives nothing — the days
  // column is what any letter rests on.
  { key: 'months', header: 'Months', num: true, cell: c => c.monthsOfCharge.toFixed(1) },
  { key: 'division', header: 'Div', cell: c => c.division },
  {
    key: 'priority', header: 'Priority',
    // Which ordering produced this number — never just the number.
    cell: c => (
      <>
        <span className="score">
          <b>{c.priorityScore}</b><i><em style={{ width: `${c.priorityScore}%` }} /></i>
        </span>
        <div className="muted xs">{c.priorityBasis?.basis ?? '—'}</div>
      </>
    ),
  },
  {
    key: 'assigned', header: 'Assigned',
    cell: c => ROLE_LABELS[c.assignedRole ?? ''] ?? c.assignedRole ?? '—',
  },
  {
    key: 'next', header: 'Next step', wide: true,
    cell: c => (
      <>
        {c.nextAction}
        {/* Only when this row differs from the page. Silence means "as stated in the header",
            never "unknown" — no reading at all is stated explicitly. */}
        {rowAge(c.figuresAsOf) && <div className="xs tone-warn">{rowAge(c.figuresAsOf)}</div>}
      </>
    ),
  },
]

export default function AmsQueue() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [params, setParams] = useSearchParams()
  const [cases, setCases] = useState<ArrearsCase[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const division = params.get('division') ?? undefined
  const category = params.get('category') ?? undefined
  const bucket = params.get('bucket') ?? undefined

  const set = (k: string, v?: string) => {
    const next = new URLSearchParams(params)
    if (v && next.get(k) !== v) next.set(k, v); else next.delete(k)
    setParams(next)
  }

  useEffect(() => {
    if (!org) return
    let off = false
    setCases(null); setError(null)
    api.ams.cases(org, { division, category, bucket, limit: '100' })
      .then(d => { if (!off) setCases(d.cases) })
      .catch((e: unknown) => { if (!off) setError(e instanceof ApiError ? e.detail : String(e)) })
    return () => { off = true }
  }, [org, division, category, bucket])

  // The freshest TCMS reading on the page. Every row used to carry its own "read N min ago" line,
  // and since the whole page is normally read in one sweep that was forty identical lines of
  // chrome under forty different next steps. Stating it once is not a simplification of the truth
  // — a row whose own figure is materially staler than this still says so, below.
  const newest = cases?.reduce<string | null>(
    (a, c) => (c.figuresAsOf && (!a || c.figuresAsOf > a) ? c.figuresAsOf : a), null) ?? null
  const STALE_MS = 10 * 60 * 1000
  // A row is called out when its figure is materially older than the page's — or when it has NO
  // reading, which is not a fresher case of the same thing but a different one, and is the only
  // state here that must never be collapsed into the header.
  const rowAge = (asOf: string | null): string | null => {
    if (!asOf) return 'No TCMS reading'
    if (newest !== null && Date.parse(newest) - Date.parse(asOf) > STALE_MS) {
      return `TCMS read ${ageOf(asOf)}`
    }
    return null
  }

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Work queue</h2>
          <p className="muted">
            Hardship cases are ordered by urgency; all others by likelihood of recovery. Each row
            says which.
            {newest && <> Balances from TCMS, read {ageOf(newest)}.</>}
          </p>
        </div>
      </header>

      <div className="filters">
        <div className="chips">
          <button className={`chip ${!division ? 'on' : ''}`} onClick={() => set('division')}>
            All divisions
          </button>
          {DIVISIONS.map(d => (
            <button key={d} className={`chip ${division === d ? 'on' : ''}`}
                    onClick={() => set('division', d)}>{d}</button>
          ))}
        </div>
        <div className="chips">
          <button className={`chip ${!category ? 'on' : ''}`} onClick={() => set('category')}>
            All categories
          </button>
          {['1', '2', '3', '4', 'unsegmented'].map(k => (
            <button key={k} className={`chip ${category === k ? 'on' : ''}`}
                    onClick={() => set('category', k)}>{CATEGORIES[k]}</button>
          ))}
        </div>
        <div className="chips">
          <button className={`chip ${!bucket ? 'on' : ''}`} onClick={() => set('bucket')}>
            Any age
          </button>
          {['d30', 'd60', 'd90'].map(k => (
            <button key={k} className={`chip ${bucket === k ? 'on' : ''}`}
                    onClick={() => set('bucket', k)}>{BUCKETS[k]}</button>
          ))}
        </div>
      </div>

      {error && <div className="page-state page-state-error">Could not load: {error}</div>}
      {!error && !cases && <div className="page-state">Loading…</div>}

      {cases && (
        <section className="card">
          <DataTable
            rows={cases}
            rowKey={c => c.id}
            empty="No cases match these filters. This is a real result, not a failed load."
            columns={COLUMNS(rowAge)}
          />
        </section>
      )}
    </div>
  )
}
